import { Button } from '@avanta/shared/components/ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@avanta/shared/components/ui/Tooltip';
import { LOCIZE_NS, type Languages } from '@avanta/shared/constants/common';
import { CONTRACT_TYPE } from '@avanta/shared/constants/psychologists';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronDown, CircleQuestionMark } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'stores/user';
import { PsychologistLanguages } from './components/PsychologistLanguages';
import { Rating } from './components/Rating';
import { type PsychoCardProps } from './types';

const MAX_PRESENTATION_LENGTH = 120;

export const PsychoCard = ({ psychologist, onClick }: PsychoCardProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const appLanguage = useUserStore(state => state.user?.appLanguage);
  const presentation = psychologist.presentations[appLanguage as Languages];
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const shouldShowReadMoreButton = Boolean(
    presentation?.length && presentation.length > MAX_PRESENTATION_LENGTH
  );

  const RatingComponent = (
    <Rating rating={psychologist.averageRate} numberOfRatings={psychologist.numberOfRatings} />
  );

  const handleOnReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const isFreelance =
    psychologist.contractType === CONTRACT_TYPE.FREELANCE &&
    psychologist.country?.toLocaleUpperCase() !== 'ES';

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'flex gap-2 p-2 am-lg:p-4 rounded-xl w-full text-left bg-white border border-teal-300'
        )}
      >
        <img
          src={psychologist.imageCircular}
          alt={psychologist.name}
          className="size-18 rounded-full hidden am-lg:block"
        />
        <div className="w-full">
          <div className="absolute top-2 right-2 am-lg:hidden">{RatingComponent}</div>
          <div className="flex justify-between items-start gap-2">
            <div className="flex items-center gap-2">
              <img
                src={psychologist.imageCircular}
                alt={psychologist.name}
                className="size-14 rounded-full am-lg:hidden"
              />
              <div className="flex flex-col am-lg:mb-4 am-lg:justify-start justify-center">
                <span className="text-sm am-lg:text-base font-medium text-denim-800 leading-5">
                  {psychologist.name} {psychologist.surname}
                </span>
                {psychologist.professionalNumber && (
                  <p className="text-xs font-medium text-gray-400 leading-4">
                    {t('AppointmentsReserve.PsychoCard.professionalNumber', {
                      professionalNumber: psychologist.professionalNumber,
                    })}
                  </p>
                )}
              </div>
            </div>
            <div className="hidden am-lg:block">{RatingComponent}</div>
          </div>
          {presentation && (
            <div className={cn('flex flex-col am-lg:gap-3 gap-2 am-lg:mb-6 mb-2 p-2 am-lg:p-0')}>
              <p
                className={cn(
                  'text-xs am-lg:text-sm font-normal line-clamp-2 transition-all duration-300 leading-4',
                  isOpen && 'line-clamp-none'
                )}
              >
                {presentation}
              </p>
              {shouldShowReadMoreButton && (
                <button
                  type="button"
                  className="w-fit text-sm text-teal-600 font-medium justify-start gap-1 flex items-center hover:underline underline-offset-4"
                  aria-expanded={isOpen}
                  onClick={handleOnReadMoreClick}
                >
                  {t('AppointmentsReserve.PsychoCard.readMore')}
                  <ChevronDown
                    className={cn(
                      'size-4 shrink-0 transition-transform duration-300',
                      isOpen && 'rotate-180'
                    )}
                  />
                </button>
              )}
            </div>
          )}
          <div
            className={cn(
              'px-2 am-lg:px-0',
              isFreelance && (onClick ? 'mb-10' : 'mb-6 am-lg:mb-0'),
              onClick && 'flex flex-col gap-4 justify-between am-lg:flex-row am-lg:items-center',
              !presentation && 'mt-2 am-lg:mt-4'
            )}
          >
            <PsychologistLanguages spokenLanguages={psychologist.spokenLanguages} />
            {onClick && (
              <>
                <Button variant="primary" size="md" onClick={onClick} className="am-lg:hidden">
                  {t('AppointmentsReserve.PsychologistForm.cta')}
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onClick}
                  className="hidden am-lg:block"
                >
                  {t('AppointmentsReserve.PsychologistForm.cta')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {isFreelance && (
        <div className="pointer-events-none absolute inset-0 flex items-end justify-end rounded-xl am-lg:p-4 p-2">
          <div className="pointer-events-auto flex items-center gap-1 cursor-default">
            <Tooltip>
              <TooltipTrigger className="flex items-center gap-1">
                <span className="text-xs font-medium text-denim-300 leading-5">
                  {t('AppointmentsReserve.PsychoCard.emotionalSupport.label')}
                </span>
                <CircleQuestionMark aria-hidden className="size-4 text-denim-300" />
              </TooltipTrigger>
              <TooltipContent
                className="max-w-68 text-denim-500 text-xs leading-4"
                side="top"
                align="start"
                sideOffset={4}
                alignOffset={-20}
              >
                {t('AppointmentsReserve.PsychoCard.emotionalSupport.content')}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};
